// Re-export enum values from the Drizzle schema as typed constants.
// Use these in service code instead of raw strings to get type-safe
// Drizzle eq() calls without falling back to sql`` fragments.

export const tourOperatorType = {
  Driver: 'Driver' as const,
  Guide: 'Guide' as const,
};

export type TourOperatorType =
  (typeof tourOperatorType)[keyof typeof tourOperatorType];

// Product service type. Plain `text` column in the DB (no pgEnum) — these are
// the hardcoded values the product form offers. See product-edit-prd
// `01-tab-supplier.md` / `14-reference-data.md`.
export const serviceType = {
  Full: 'Full' as const,
  Half: 'Half' as const,
  Transfer: 'Transfer' as const,
};

export type ServiceType = (typeof serviceType)[keyof typeof serviceType];

// `as const` tuple so it can drive `z.enum(...)` and keep the literal union.
export const serviceTypeValues = [
  serviceType.Full,
  serviceType.Half,
  serviceType.Transfer,
] as const;

// Product transport (car) type. Plain `text` column in the DB (no pgEnum).
// The legacy v2 car-type set (v2 `TransportationCarType`, TP-4064) plus Train.
// Every surface that offers a car type — the Operations grid, edit modal and
// filter, the Planner Transport subitem form, the product Supplier tab, and
// the agent ontology — reads this list.
export const transportType = {
  Sedan: 'Sedan' as const,
  Minivan: 'Minivan' as const,
  Minibus: 'Minibus' as const,
  Bus: 'Bus' as const,
  SUV: 'SUV' as const,
  Sprinter: 'Sprinter' as const,
  SedanClassE: 'Sedan class E' as const,
  SedanClassS: 'Sedan class S' as const,
  TukTuk: 'TukTuk' as const,
  Train: 'Train' as const,
};

export type TransportType = (typeof transportType)[keyof typeof transportType];

export const transportTypeValues = [
  transportType.Sedan,
  transportType.Minivan,
  transportType.Minibus,
  transportType.Bus,
  transportType.SUV,
  transportType.Sprinter,
  transportType.SedanClassE,
  transportType.SedanClassS,
  transportType.TukTuk,
  transportType.Train,
] as const;

// Map marker style for a product location (`legs[].locus.locations[].markerType`).
export const markerType = {
  Routed: 'Routed' as const,
  NotRouted: 'NotRouted' as const,
  None: 'None' as const,
};

export type MarkerType = (typeof markerType)[keyof typeof markerType];

export const markerTypeValues = [
  markerType.Routed,
  markerType.NotRouted,
  markerType.None,
] as const;

export const category = {
  Other: 'Other' as const,
  Attraction: 'Attraction' as const,
  Personnel: 'Personnel' as const,
  Transportation: 'Transportation' as const,
  Accommodation: 'Accommodation' as const,
  Venue: 'Venue' as const,
  Information: 'Information' as const,
  Destination: 'Destination' as const,
  Dining: 'Dining' as const,
  Activity: 'Activity' as const,
};

export type Category = (typeof category)[keyof typeof category];

export const categoryValues = Object.values(category);

export const subcategory = {
  Homestay: 'Homestay' as const,
  PackedFood: 'PackedFood' as const,
  AdministrativeArea: 'AdministrativeArea' as const,
  PopularSite: 'PopularSite' as const,
  NationalPark: 'NationalPark' as const,
  Van: 'Van' as const,
  Shopping: 'Shopping' as const,
  Gift: 'Gift' as const,
  GolfCourse: 'GolfCourse' as const,
  Apartment: 'Apartment' as const,
  Entertainer: 'Entertainer' as const,
  Guide: 'Guide' as const,
  DriverServices: 'DriverServices' as const,
  Sport: 'Sport' as const,
  Train: 'Train' as const,
  SwimmingPool: 'SwimmingPool' as const,
  Inn: 'Inn' as const,
  Activity: 'Activity' as const,
  Camp: 'Camp' as const,
  Site: 'Site' as const,
  FoodMarket: 'FoodMarket' as const,
  AmusementPark: 'AmusementPark' as const,
  Cottage: 'Cottage' as const,
  Attraction: 'Attraction' as const,
  Safari: 'Safari' as const,
  Ferry: 'Ferry' as const,
  Bar: 'Bar' as const,
  Lodge: 'Lodge' as const,
  Cruise: 'Cruise' as const,
  OutdoorEquipment: 'OutdoorEquipment' as const,
  Bungalow: 'Bungalow' as const,
  Castle: 'Castle' as const,
  Hostel: 'Hostel' as const,
  Flight: 'Flight' as const,
  Tour: 'Tour' as const,
  Dining: 'Dining' as const,
  Cultural: 'Cultural' as const,
  Museum: 'Museum' as const,
  Leisure: 'Leisure' as const,
  Event: 'Event' as const,
  Taxi: 'Taxi' as const,
  SpaceRental: 'SpaceRental' as const,
  Poi: 'Poi' as const,
  Villa: 'Villa' as const,
  Cycling: 'Cycling' as const,
  Boutique: 'Boutique' as const,
  ReligiousSite: 'ReligiousSite' as const,
  Sightseeing: 'Sightseeing' as const,
  Information: 'Information' as const,
  Coffee: 'Coffee' as const,
  Guesthouse: 'Guesthouse' as const,
  Beach: 'Beach' as const,
  Helicopter: 'Helicopter' as const,
  Conference: 'Conference' as const,
  CampingSite: 'CampingSite' as const,
  ArtGallery: 'ArtGallery' as const,
  Zoo: 'Zoo' as const,
  Venue: 'Venue' as const,
  Wine: 'Wine' as const,
  Catering: 'Catering' as const,
  BB: 'BB' as const,
  CookingCourse: 'CookingCourse' as const,
  Country: 'Country' as const,
  TeamActivities: 'TeamActivities' as const,
  Festival: 'Festival' as const,
  Bus: 'Bus' as const,
  Motorcycle: 'Motorcycle' as const,
  AudioVideoEquipment: 'AudioVideoEquipment' as const,
  Bakery: 'Bakery' as const,
  Gym: 'Gym' as const,
  RentalCar: 'RentalCar' as const,
  Spa: 'Spa' as const,
  ThemeParty: 'ThemeParty' as const,
  BoatActivity: 'BoatActivity' as const,
  City: 'City' as const,
  Resort: 'Resort' as const,
  Hiking: 'Hiking' as const,
  Hotel: 'Hotel' as const,
  FishingHunting: 'FishingHunting' as const,
  Ticket: 'Ticket' as const,
  Ecoresort: 'Ecoresort' as const,
  HotelBoutique: 'HotelBoutique' as const,
  HotelDeCharme: 'HotelDeCharme' as const,
  HotelVila: 'HotelVila' as const,
  Pousada: 'Pousada' as const,
  NaturalPhenomenon: 'NaturalPhenomenon' as const,
  NatureReserve: 'NatureReserve' as const,
  Pizzeria: 'Pizzeria' as const,
  Restaurant: 'Restaurant' as const,
  CityGuide: 'CityGuide' as const,
  Equipment: 'Equipment' as const,
  Other: 'Other' as const,
  Speaker: 'Speaker' as const,
  HolidayVillage: 'HolidayVillage' as const,
  Fixer: 'Fixer' as const,
  Boat: 'Boat' as const,
};

export type Subcategory = (typeof subcategory)[keyof typeof subcategory];

export const subcategoryValues = Object.values(subcategory);

// Static mapping of category → allowed subcategories. Ported from legacy
// legacy travel_planner_back `src/common/enum/category.ts:CategorySubcategories`.
// Used by the products list-filter category-partition logic so a category
// without any matching subcategory in the user's subcategory input matches
// freely, while a category with overlap is narrowed to the intersection.
// Legacy values that don't exist in the new `subcategory` enum (e.g. `Sedan`)
// are simply omitted — the partition logic treats them as no-op entries.
export const CategorySubcategories: Record<Category, readonly Subcategory[]> = {
  [category.Other]: [
    subcategory.AudioVideoEquipment,
    subcategory.OutdoorEquipment,
    subcategory.Gift,
    subcategory.Ticket,
    subcategory.Other,
  ],
  [category.Attraction]: [
    subcategory.Beach,
    subcategory.CampingSite,
    subcategory.Castle,
    subcategory.Festival,
    subcategory.GolfCourse,
    subcategory.NationalPark,
    subcategory.Poi,
    subcategory.PopularSite,
    subcategory.ReligiousSite,
    subcategory.Shopping,
    subcategory.Site,
    subcategory.AdministrativeArea,
    subcategory.NaturalPhenomenon,
    subcategory.NatureReserve,
    subcategory.Other,
    subcategory.Attraction,
  ],
  [category.Personnel]: [
    subcategory.Entertainer,
    subcategory.Guide,
    subcategory.Other,
    subcategory.Speaker,
    subcategory.Fixer,
  ],
  [category.Transportation]: [
    subcategory.Bus,
    subcategory.Cruise,
    subcategory.Cycling,
    subcategory.Ferry,
    subcategory.Flight,
    subcategory.Helicopter,
    subcategory.DriverServices,
    subcategory.RentalCar,
    subcategory.Taxi,
    subcategory.Train,
    subcategory.Motorcycle,
    subcategory.Van,
    subcategory.Other,
  ],
  [category.Accommodation]: [
    subcategory.Apartment,
    subcategory.Bungalow,
    subcategory.Boutique,
    subcategory.Camp,
    subcategory.CampingSite,
    subcategory.Cottage,
    subcategory.Guesthouse,
    subcategory.Homestay,
    subcategory.Hostel,
    subcategory.Hotel,
    subcategory.Inn,
    subcategory.Lodge,
    subcategory.Resort,
    subcategory.Villa,
    subcategory.Ecoresort,
    subcategory.HotelBoutique,
    subcategory.HotelDeCharme,
    subcategory.HotelVila,
    subcategory.Pousada,
    subcategory.Other,
    subcategory.BB,
    subcategory.HolidayVillage,
    subcategory.Boat,
  ],
  [category.Venue]: [
    subcategory.Conference,
    subcategory.SpaceRental,
    subcategory.Venue,
    subcategory.Equipment,
    subcategory.Other,
  ],
  [category.Information]: [
    subcategory.Information,
    subcategory.CityGuide,
    subcategory.Other,
  ],
  [category.Destination]: [
    subcategory.Country,
    subcategory.City,
    subcategory.Other,
  ],
  [category.Dining]: [
    subcategory.Bakery,
    subcategory.Bar,
    subcategory.Catering,
    subcategory.Coffee,
    subcategory.Dining,
    subcategory.FoodMarket,
    subcategory.PackedFood,
    subcategory.Pizzeria,
    subcategory.Restaurant,
    subcategory.Other,
  ],
  [category.Activity]: [
    subcategory.Activity,
    subcategory.AmusementPark,
    subcategory.ArtGallery,
    subcategory.BoatActivity,
    subcategory.CookingCourse,
    subcategory.Cultural,
    subcategory.Event,
    subcategory.FishingHunting,
    subcategory.Gym,
    subcategory.Hiking,
    subcategory.Leisure,
    subcategory.Museum,
    subcategory.Safari,
    subcategory.Sightseeing,
    subcategory.Spa,
    subcategory.Sport,
    subcategory.SwimmingPool,
    subcategory.TeamActivities,
    subcategory.ThemeParty,
    subcategory.Tour,
    subcategory.Wine,
    subcategory.Zoo,
    subcategory.Other,
  ],
};
